import { useCallback, useEffect, useState } from "react";
import type { BatchJobEntry, BatchJobStatus } from "@models/batch";
import type { TranslationHistoryItem } from "@models/translation";
import { batchApi } from "@services/batchApi";

// Fetch up to the backend's max (200) so client-side pagination has the full
// job history to page through.
const MAX_ENTRIES = 200;

/**
 * Map a backend batch history item to a local BatchJobEntry.
 *
 * The history record holds only metadata (no live status/progress), so we seed
 * the entry as "submitted" with unknown progress. The view's poller then
 * refreshes each non-terminal job (and completed jobs without a download URL)
 * to fill in status, progress and the presigned download URL.
 */
const toEntry = (item: TranslationHistoryItem): BatchJobEntry => ({
  jobId: item.id,
  fileName: item.file_name ?? item.preview ?? item.id,
  sourceLang: item.source_lang,
  targetLang: item.target_langs[0] ?? "",
  model: item.model ?? "",
  totalUnits: 0,
  status: "submitted" as BatchJobStatus,
  completedUnits: 0,
  createdAt: item.created_at,
  updatedAt: item.updated_at,
  contentHash: item.content_hash,
});

/**
 * Backend-backed batch job history for the authenticated user.
 *
 * Batch jobs are persisted server-side (keyed by user id) so they follow the
 * user across devices and sessions. The backend records an entry when a batch
 * is started; this hook loads that history and supports optimistic updates for
 * the live submit/poll flow. Keeps the same shape
 * ({ jobs, add, update, remove, clear }) as the previous localStorage
 * implementation so consumers need no changes.
 */
export const useBatchJobs = () => {
  const [jobs, setJobs] = useState<BatchJobEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await batchApi.listBatchHistory(MAX_ENTRIES);
      setJobs((prev) => {
        // Preserve any live in-memory progress/status/resultsUrl we already
        // have for a job (the server history only stores metadata).
        const byId = new Map(prev.map((j) => [j.jobId, j]));
        return res.items.map((item) => {
          const existing = byId.get(item.id);
          const base = toEntry(item);
          return existing ? { ...base, ...existing, contentHash: base.contentHash } : base;
        });
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load batch history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Optimistically show a newly submitted job; the server has already recorded
  // it on startBatchTranslation. Dedupe by jobId.
  const add = useCallback((entry: BatchJobEntry) => {
    setJobs((prev) =>
      [entry, ...prev.filter((e) => e.jobId !== entry.jobId)].slice(0, MAX_ENTRIES),
    );
  }, []);

  const update = useCallback((jobId: string, patch: Partial<BatchJobEntry>) => {
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.jobId === jobId);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch, updatedAt: new Date().toISOString() };
      return next;
    });
  }, []);

  const remove = useCallback(
    async (jobId: string) => {
      const target = jobs.find((j) => j.jobId === jobId);
      setJobs((prev) => prev.filter((e) => e.jobId !== jobId));
      if (target?.contentHash) {
        try {
          await batchApi.deleteBatchHistoryEntry(target.contentHash);
        } catch {
          void refresh();
        }
      }
    },
    [jobs, refresh],
  );

  const clear = useCallback(async () => {
    const previous = jobs;
    setJobs([]);
    try {
      // Delete each batch entry server-side by its history sort key.
      await Promise.all(
        previous
          .filter((j) => j.contentHash)
          .map((j) => batchApi.deleteBatchHistoryEntry(j.contentHash as string)),
      );
    } catch {
      setJobs(previous);
      void refresh();
    }
  }, [jobs, refresh]);

  return { jobs, add, update, remove, clear, loading, error, refresh };
};
